/**
 * Alt+. module launcher — keyboard-first ERP navigation (plan P2).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { KEYBOARD_EVENTS } from "@/lib/keyboard/keyboard-events";
import {
  buildNavigationLauncherCatalog,
  filterLauncherCatalog,
} from "@/lib/navigation-launcher-catalog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export function ModuleLauncher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const catalog = useMemo(() => buildNavigationLauncherCatalog(), []);

  useEffect(() => {
    function onOpen() {
      setOpen(true);
      setQuery("");
    }
    window.addEventListener(KEYBOARD_EVENTS.MODULE_LAUNCHER, onOpen);
    return () => window.removeEventListener(KEYBOARD_EVENTS.MODULE_LAUNCHER, onOpen);
  }, []);

  const items = useMemo(
    () => filterLauncherCatalog(catalog, query).slice(0, 40),
    [catalog, query],
  );

  function goTo(item: (typeof items)[number]) {
    setOpen(false);
    void navigate({
      to: item.to as "/",
      search: (item.search ?? undefined) as never,
      params: (item.params ?? undefined) as never,
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="module-launcher">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="text-sm">Module Launcher</DialogTitle>
          <DialogDescription className="text-xs">
            Jump anywhere in the ERP catalog. Press <Badge variant="outline" className="mx-1">Alt+.</Badge> anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 pb-2">
          <Input
            autoFocus
            placeholder="Type module or screen name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && items[0]) goTo(items[0]);
            }}
          />
        </div>
        <ul className="max-h-72 overflow-y-auto border-t">
          {items.length === 0 ? (
            <li className="p-4 text-sm text-muted-foreground">No matching destinations.</li>
          ) : (
            items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="w-full text-left px-4 py-3 hover:bg-muted/60 transition-colors"
                  onClick={() => goTo(item)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{item.label}</span>
                    <span className="text-[10px] text-muted-foreground">{item.category}</span>
                  </div>
                  {item.description ? (
                    <div className="text-xs text-muted-foreground line-clamp-1">{item.description}</div>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
